import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { saveAs } from 'file-saver';
import { UserService } from 'src/app/shared/services/user/user.service';
import { AgentConfig, MEDIGI_AGENT } from '../agents.config';
import { environment } from 'src/environments/environment';

export interface AgentSource {
  title: string;
  url: string;
  snippet: string;
}

/** Side-effect performed by a specialist during this turn — e.g. an email
 * dispatched or a support ticket queued. Rendered as a confirmation chip
 * so the user can see what actually happened, not just the agent's text. */
export interface AgentSideEffect {
  kind: 'email_sent' | 'escalation_queued' | string;
  detail: string;
}

export interface ChartSpec {
  chartType: 'bar' | 'line' | 'area' | 'pie';
  title: string;
  argumentField: string;
  valueFields: string[];
  data: Record<string, any>[];
}

/** Provenance for a section: how it was built + why it was picked. */
export interface ReportSectionMethod {
  how?: string;
  why?: string;
}

/** One rendered card in a multi-section report (V2 report pipeline). */
export interface ReportSection {
  type: 'kpi' | 'chart' | 'grid' | 'text';
  title?: string;
  narrative?: string;
  data?: any;
  method?: ReportSectionMethod;
}

/** Live state of an async report (polled from /agent/report/<id>). */
export interface ReportState {
  reportId: string;
  status: string;          // queued|planning|running|synthesizing|done|error
  plan?: { title?: string }[];
  sections: ReportSection[];
  /** Report-level suggested next questions (chips), surfaced when done. */
  followups?: string[];
  /** Company id this report is about — threaded into follow-up chips so they
   * keep the company context (chip text has no company id of its own). */
  subjectCompanyId?: string | number;
  completed: number;
  total: number;
  error?: string;
}

export interface ChatMessage {
  role: 'user' | 'agent';
  text: string;
  sources?: AgentSource[];
  sideEffects?: AgentSideEffect[];
  chart?: ChartSpec;
  personas?: string[];
  /** Live progress trail while the agent is still working (SSE only). */
  progress?: string[];
  attachments?: ChatAttachment[];
  /** Present when this message is an async multi-section report. */
  report?: ReportState;
  timestamp: string;
  pending?: boolean;
  error?: string;
  errorStatus?: number;
}

/** A file the user attached for this conversation. The upload happens
 *  before send (so the URL is known when the chat POST goes out). */
export interface ChatAttachment {
  url: string;
  filename: string;
  mime?: string;
  size_bytes?: number;
  /** Which upload button the user pressed. Tells Facility Manager which
   *  flow to take (state-license vs dea-license parsing). */
  license_type?: 'state_license' | 'dea_license';
}

interface PersistedMessage extends Omit<ChatMessage, 'pending'> {}

const BUFFER_CAP = 20;

@Component({
  selector: 'app-agent-chat',
  templateUrl: './agent-chat.component.html',
  styleUrls: ['./agent-chat.component.scss'],
})
export class AgentChatComponent implements OnInit {
  @Output() close = new EventEmitter<void>();

  /** Static — there's currently a single Medigi agent. The multi-agent
   * surface (route param, sidebar selector, agentKey input) was removed; this
   * field is kept for forward compatibility with the existing template. */
  readonly agent: AgentConfig = MEDIGI_AGENT;

  @Input() displayName = 'Support';
  @Input() showClose = false;
  @Input() showEscalate = true;
  /** Show the "expand to fullscreen" button. Set true in the sidebar popup. */
  @Input() showExpand = false;

  messages: ChatMessage[] = [];
  draft: string = '';
  sending: boolean = false;
  trimmedThisSession: boolean = false;
  pendingAttachments: ChatAttachment[] = [];
  uploadingLicenseType: 'state_license' | 'dea_license' | null = null;
  uploadError: string | null = null;
  currentUser: {
    id: number | string;
    first_name?: string;
    last_name?: string;
    email?: string;
    is_buyer?: boolean;
    is_admin?: boolean;
  } | null = null;

  constructor(
    private router: Router,
    private userService: UserService,
  ) {}

  ngOnInit(): void {
    this.loadCurrentUser();
    this.messages = this.loadFromStorage();
  }

  get headerName(): string {
    return this.displayName || this.agent.name;
  }

  /** License uploads are a buyer-only affordance — facility license docs only
   * make sense for buyer accounts (admins/sellers manage docs elsewhere). */
  get isBuyer(): boolean {
    return this.currentUser?.is_buyer === true;
  }

  /** Reports are an admin-only surface (cross-company analytics). */
  get isAdmin(): boolean {
    return this.currentUser?.is_admin === true;
  }

  get canReport(): boolean {
    return this.isAdmin && !this.sending && this.draft.trim().length > 0;
  }

  /** High-precision report detector: requires an explicit company id AND
   * report-grade phrasing, so a normal question ("where is order 123?")
   * stays in chat. Used to auto-route admins to the report pipeline. */
  private looksLikeReport(text: string): boolean {
    if (!text) return false;
    const hasCompanyId =
      /\b(?:buyer|seller)\s+(?:company\s+)?(?:id\s*)?#?\s*\d+/i.test(text) ||
      /\bcompany\s+(?:id\s*)?#?\s*\d+/i.test(text);
    if (!hasCompanyId) return false;
    const reportish =
      /\b(report|overview|deep[\s-]?dive|performance|how\s+(is|are|does|did|do)|recommendation|top\s+\d+|moving items|best\s+(buyer|facilit)|breakdown|summary)\b/i;
    return reportish.test(text);
  }

  get canEscalate(): boolean {
    return this.messages.some(m => m.role === 'user');
  }

  /** Kick off an async multi-section report from the current draft. POSTs to
   * /report, drops a report message, then polls until done/error. */
  runReport(explicitCompanyId?: string | number): void {
    if (!this.canReport) return;
    const question = this.draft.trim();
    this.draft = '';
    // A report is scoped to ONE company. A typed request carries the id in the
    // text; a follow-up chip (whose text has no company) passes the originating
    // report's id so the context isn't lost.
    const companyId = explicitCompanyId ?? this.extractCompanyId(question);

    this.pushMessage({
      role: 'user',
      text: question,
      timestamp: new Date().toISOString(),
    });
    const placeholder: ChatMessage = {
      role: 'agent',
      text: '',
      timestamp: new Date().toISOString(),
      pending: true,
      report: {
        reportId: '', status: 'queued', sections: [], followups: [],
        subjectCompanyId: companyId, completed: 0, total: 0,
      },
    };
    this.pushMessage(placeholder);
    this.persist();

    const token = localStorage.getItem('auth0Token');
    if (!token || !this.agent?.webhookUrl) {
      this.failReport(placeholder, 'Could not obtain auth token. Please log back in.');
      return;
    }

    this.sending = true;
    const hostname = window.location.hostname;
    fetch(this.agent.webhookUrl + '/report', {
      method: 'POST',
      credentials: 'include',
      headers: this.authHeaders(token, hostname),
      body: JSON.stringify({ question, sessionId: this.sessionId, companyId }),
    })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(res => {
        const reportId = res?.data?.report_id;
        if (!reportId) { throw new Error('no report_id'); }
        placeholder.report!.reportId = reportId;
        placeholder.report!.status = res?.data?.status || 'queued';
        this.pollReport(reportId, placeholder, 0);
      })
      .catch(status => {
        this.failReport(placeholder,
          typeof status === 'number' ? `Report request failed (${status}).`
                                     : 'Could not start the report.');
      });
  }

  /** Click handler for a suggested follow-up chip. Submits the chip text as a
   * brand-new report request by reusing the existing report submit flow
   * (populate the draft, then runReport()). No-op while a report is in flight. */
  askFollowup(question: string, rep?: ReportState, originatingText?: string): void {
    if (this.sending || !question) return;
    this.draft = question;
    // Carry the originating report's company id — the chip text has none. Prefer
    // the id captured on the report; fall back to parsing the request that
    // spawned it (covers reports created before this was stored).
    const companyId = rep?.subjectCompanyId ?? this.extractCompanyId(originatingText || '');
    this.runReport(companyId);
  }

  /** Regenerate a finished report by re-asking its original request. Reuses the
   * report's company id so scoping is preserved even if the text needs it. */
  rerunReport(rep?: ReportState, originatingText?: string): void {
    if (this.sending) return;
    const q = (originatingText || '').trim();
    if (!q) return;
    this.draft = q;
    this.runReport(rep?.subjectCompanyId);
  }

  /** Best-effort parse of a company id from a report request, mirroring the
   * backend parser ("buyer company 373", "company 373"). Lets follow-up chips
   * inherit the company context even before the backend echoes it back. */
  private extractCompanyId(text: string): string | undefined {
    const t = text || '';
    const m = t.match(/\b(?:buyer|seller)?\s*company\s+(\d+)\b/i)
           || t.match(/\b(?:buyer|seller)\s+(\d+)\b/i);
    return m ? m[1] : undefined;
  }

  /** Tooltip text for report charts (pie + line/bar). Surfaces the entity NAME
   * (facility, buyer/company, seller, NDC) — the argument — alongside the
   * formatted value and, for pies, the percentage, so a slice/point is
   * identifiable. Arrow fn so it can be passed straight to DevExtreme's
   * [customizeTooltip] without losing context. */
  reportChartTooltip = (info: any): { text: string } => {
    const name = info?.argumentText ?? info?.argument ?? '';
    const value = info?.valueText ?? (info?.value != null ? info.value : '');
    const pct = info?.percentText ? ` (${info.percentText})` : '';
    const label = name !== '' ? `${name}: ${value}` : `${value}`;
    return { text: `${label}${pct}` };
  };

  /** Pie slice label: show the percentage ONLY for slices over 10% (the big
   * ones), blank otherwise — keeps the pie uncluttered while the connector
   * line points at each labelled slice. Full detail (name + $ + %) is in the
   * tooltip. Arrow fn for DevExtreme's [customizeText]. */
  piePercentLabel = (pointInfo: any): string => {
    const pct = pointInfo?.percent ?? 0;
    return pct > 0.1 ? (pointInfo?.percentText || `${Math.round(pct * 100)}%`) : '';
  };

  /** Assemble a finished report into a clean, copy-pasteable Markdown string —
   * the "feed the report to another AI" use case. Pure (no side effects). */
  reportToMarkdown(rep: ReportState, title?: string): string {
    const out: string[] = [];
    const heading = (title || 'Company report').trim();
    out.push(`# ${heading}`);

    for (const sec of rep.sections || []) {
      const secTitle = (sec.title || '').trim() || 'Section';
      out.push('', `## ${secTitle}`);

      if (sec.type === 'kpi') {
        const items = sec.data?.items || [];
        for (const it of items) {
          const val = it?.value === null || it?.value === undefined ? '—' : it.value;
          out.push(`- **${it?.label ?? ''}:** ${val}`);
        }
      } else if (sec.type === 'grid') {
        out.push(this.rowsToMarkdownTable(sec.data?.columns, sec.data?.rows));
      } else if (sec.type === 'chart') {
        const chartType = sec.data?.chartType || 'chart';
        out.push(`_Chart: ${chartType}._`, '');
        // Columns synthesized from the value fields so an AI can read the data.
        const cols = [
          { field: sec.data?.argumentField, caption: sec.data?.argumentField },
          ...(sec.data?.valueFields || []).map((f: string) => ({ field: f, caption: f })),
        ].filter(c => c.field);
        out.push(this.rowsToMarkdownTable(cols, sec.data?.rows));
      } else if (sec.type === 'text') {
        out.push((sec.narrative || '').trim());
      }

      if (sec.method && (sec.method.how || sec.method.why)) {
        const prov = [sec.method.how, sec.method.why].filter(Boolean).join(' ');
        out.push('', `_Built from ${prov}_`);
      }
    }

    const followups = rep.followups || [];
    if (followups.length) {
      out.push('', '## Suggested follow-ups');
      for (const q of followups) out.push(`- ${q}`);
    }

    return out.join('\n') + '\n';
  }

  /** Render columns + rows as a GitHub-flavored Markdown table. Columns use
   * `caption` for the header and `field` to pull each row's value. Returns ''
   * when there are no columns/rows so callers can skip empty tables. */
  private rowsToMarkdownTable(columns: any[] | undefined, rows: any[] | undefined): string {
    const cols = columns || [];
    const data = rows || [];
    if (!cols.length || !data.length) return '_No data._';
    const esc = (v: any): string => {
      if (v === null || v === undefined) return '';
      return String(v).replace(/\|/g, '\\|').replace(/\n/g, ' ');
    };
    const header = `| ${cols.map(c => esc(c.caption ?? c.field)).join(' | ')} |`;
    const sep = `| ${cols.map(() => '---').join(' | ')} |`;
    const body = data.map(
      r => `| ${cols.map(c => esc(r?.[c.field])).join(' | ')} |`,
    );
    return [header, sep, ...body].join('\n');
  }

  /** Download a finished report as a .md file via file-saver's saveAs (same
   * pattern used elsewhere in the app). Filename slugged from the report id. */
  downloadReportAsMarkdown(rep: ReportState, title?: string): void {
    const md = this.reportToMarkdown(rep, title);
    const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
    const slug = (rep.reportId || 'company').replace(/[^a-z0-9]+/gi, '_').slice(0, 40) || 'company';
    saveAs(blob, `report_${slug}.md`);
  }

  /** Mark the report message as failed — the error shows inside the report
   * card (the normal error block is gated off when m.report is present). */
  private failReport(msg: ChatMessage, error: string): void {
    this.sending = false;
    msg.pending = false;
    if (msg.report) {
      msg.report.status = 'error';
      msg.report.error = error;
    } else {
      msg.error = error;
    }
    this.persist();
  }

  private pollReport(reportId: string, msg: ChatMessage, since: number): void {
    const token = localStorage.getItem('auth0Token');
    if (!token || !this.agent?.webhookUrl) { this.sending = false; return; }
    const hostname = window.location.hostname;

    fetch(`${this.agent.webhookUrl}/report/${reportId}?since=${since}`, {
      method: 'GET',
      credentials: 'include',
      headers: this.authHeaders(token, hostname),
    })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(res => {
        const d = res?.data;
        if (!d || !msg.report) { this.sending = false; return; }
        if (Array.isArray(d.sections) && d.sections.length) {
          msg.report.sections.push(...d.sections);
        }
        msg.report.status = d.status;
        msg.report.plan = d.plan || msg.report.plan;
        // Report-level follow-up chips arrive populated only once synthesis is
        // done; keep prior value if a poll omits them.
        if (Array.isArray(d.followups) && d.followups.length) {
          msg.report.followups = d.followups;
        }
        // Prefer the backend's resolved company id (authoritative) once present.
        if (d.subject_company_id != null) {
          msg.report.subjectCompanyId = d.subject_company_id;
        }
        msg.report.completed = d.completed ?? msg.report.completed;
        msg.report.total = d.total ?? msg.report.total;

        if (d.status === 'done' || d.status === 'error') {
          msg.pending = false;
          if (d.status === 'error') { msg.report.error = d.error || 'Report failed.'; }
          this.sending = false;
          this.persist();
          return;
        }
        // keep polling for new sections
        const nextSince = d.next_since ?? since;
        this.persist();
        setTimeout(() => this.pollReport(reportId, msg, nextSince), 1500);
      })
      .catch(() => {
        // transient — retry a few beats later rather than failing the whole report
        setTimeout(() => this.pollReport(reportId, msg, since), 2500);
      });
  }

  reportStatusLabel(rep: ReportState): string {
    switch (rep.status) {
      case 'queued': return 'Queued…';
      case 'planning': return 'Planning the report…';
      case 'running': return 'Pulling the data…';
      case 'synthesizing': return 'Writing the summary…';
      case 'done': return 'Report';
      case 'error': return 'Report failed';
      default: return rep.status || 'Working…';
    }
  }

  private authHeaders(token: string, hostname: string): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      SourceDomain: hostname,
      'Auth0-Client-Id': environment.auth0.clientId,
      'Client-Name-Space': environment.supplierConfig.clientNameSpace,
      CatalogType: (environment.catalogType as Record<string, string>)[hostname] || 'medigi',
    };
  }

  /** Expand the popup to the standalone support page. Persisted localStorage
   * history carries over because the storage key only depends on sessionId. */
  expandToFullscreen(): void {
    this.close.emit();
    // /support-chat is now the single standalone chat surface for both buyers
    // and admins (admins bypass the buyer RoleGuard).
    this.router.navigate(['/support-chat']);
  }

  openKbManager(): void {
    this.router.navigate(['/admin/kb-manager']);
  }

  escalating: boolean = false;

  async escalateToHuman(): Promise<void> {
    if (this.escalating) return;
    const firstUserMsg = this.messages.find(m => m.role === 'user')?.text;
    if (!firstUserMsg) return;

    this.escalating = true;

    // Ask the backend to summarize the conversation into a clean handoff
    // brief. Falls back to the first user message if the summary fails so
    // we never block the human handoff on AI flakiness.
    let handoffText = `Hi, I was chatting with the AI assistant about: "${firstUserMsg}". Can someone help?`;
    try {
      const summary = await this.fetchConversationSummary();
      if (summary) {
        handoffText = `Hi — handoff from the AI assistant. ${summary} Can someone help from here?`;
      }
    } catch {
      // swallow — we'll send the fallback greeting
    } finally {
      this.escalating = false;
    }

    const chatlio = (window as any)._chatlio;
    if (this.currentUser && chatlio?.identify) {
      chatlio.identify(this.sessionId, {
        name: this.userName,
        email: this.userEmail,
      });
    }
    if (chatlio?.send) chatlio.send(handoffText);
    if (chatlio?.open) chatlio.open();
    this.close.emit();
  }

  private async fetchConversationSummary(): Promise<string> {
    const token = localStorage.getItem('auth0Token');
    if (!token || !this.agent?.webhookUrl) return '';
    const hostname = window.location.hostname;
    const res = await fetch(this.agent.webhookUrl + '/summarize', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        SourceDomain: hostname,
        'Auth0-Client-Id': environment.auth0.clientId,
        'Client-Name-Space': environment.supplierConfig.clientNameSpace,
        CatalogType: (environment.catalogType as Record<string, string>)[hostname] || 'medigi',
      },
      body: JSON.stringify({ sessionId: this.sessionId }),
    });
    if (!res.ok) return '';
    const json = await res.json();
    const text = json?.data?.text;
    return typeof text === 'string' ? text.trim() : '';
  }

  private loadCurrentUser(): void {
    this.userService.getMe().subscribe(
      result => (this.currentUser = result?.data ?? null),
      () => (this.currentUser = null),
    );
  }

  get sessionId(): string {
    return this.currentUser?.id != null ? String(this.currentUser.id) : 'anonymous';
  }

  get userName(): string {
    if (!this.currentUser) return '';
    const parts = [this.currentUser.first_name, this.currentUser.last_name].filter(Boolean);
    return parts.join(' ');
  }

  get userInitials(): string {
    if (!this.currentUser) return 'U';
    const first = this.currentUser.first_name?.[0] || '';
    const last = this.currentUser.last_name?.[0] || '';
    return (first + last).toUpperCase() || 'U';
  }

  get userEmail(): string {
    return this.currentUser?.email ?? '';
  }

  get canSend(): boolean {
    if (!this.agent || this.sending || this.uploadingLicenseType) return false;
    // Either the user typed something OR they staged an attachment.
    return this.draft.trim().length > 0 || this.pendingAttachments.length > 0;
  }

  onInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  send(): void {
    if (!this.canSend) return;

    // Auto-route report-grade asks (admins only) to the async report
    // pipeline instead of a normal chat turn. Conservative: needs an explicit
    // company id AND report-y phrasing, so ordinary questions stay in chat.
    // The "Report" button remains an explicit override.
    if (this.isAdmin && this.pendingAttachments.length === 0
        && this.looksLikeReport(this.draft.trim())) {
      this.runReport();
      return;
    }

    let text = this.draft.trim();
    this.draft = '';

    // Attachment-only send: synthesize a minimal user message so the
    // agent has something to anchor on. The attachment marker carries
    // the URL + license_type.
    if (!text && this.pendingAttachments.length > 0) {
      const kinds = this.pendingAttachments
        .map(a => a.license_type)
        .filter(Boolean) as string[];
      if (kinds.includes('state_license') && kinds.includes('dea_license')) {
        text = "I'd like to upload a state license and a DEA license.";
      } else if (kinds.includes('state_license')) {
        text = "I'd like to upload a state license.";
      } else if (kinds.includes('dea_license')) {
        text = "I'd like to upload a DEA license.";
      } else {
        text = "I attached a file.";
      }
    }

    // Snapshot the attachments staged for THIS turn, then clear the
    // input bar's pending list so the next message starts fresh.
    const attachmentsForTurn = this.pendingAttachments;
    this.pendingAttachments = [];

    this.pushMessage({
      role: 'user',
      text,
      attachments: attachmentsForTurn.length ? attachmentsForTurn : undefined,
      timestamp: new Date().toISOString(),
    });
    const placeholder: ChatMessage = {
      role: 'agent',
      text: '',
      timestamp: new Date().toISOString(),
      pending: true,
    };
    this.pushMessage(placeholder);
    this.persist();

    this.dispatchToAgent(text, placeholder, attachmentsForTurn);
  }

  retry(failed: ChatMessage): void {
    const idx = this.messages.indexOf(failed);
    if (idx <= 0) return;
    const prior = this.messages[idx - 1];
    if (!prior || prior.role !== 'user') return;

    failed.pending = true;
    failed.error = undefined;
    failed.errorStatus = undefined;
    failed.text = '';
    failed.sources = undefined;
    failed.sideEffects = undefined;
    failed.chart = undefined;
    failed.personas = undefined;
    failed.progress = undefined;
    this.persist();

    this.dispatchToAgent(prior.text, failed, prior.attachments || []);
  }

  newChat(): void {
    this.messages = [];
    this.trimmedThisSession = false;
    this.clearStorage();
    this.resetServerSession();
  }

  private resetServerSession(): void {
    const token = localStorage.getItem('auth0Token');
    if (!token) return;  // best-effort; local clear already happened
    const hostname = window.location.hostname;
    fetch(this.agent.webhookUrl + '/reset', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        SourceDomain: hostname,
        'Auth0-Client-Id': environment.auth0.clientId,
        'Client-Name-Space': environment.supplierConfig.clientNameSpace,
        CatalogType: (environment.catalogType as Record<string, string>)[hostname] || 'medigi',
      },
      body: JSON.stringify({ sessionId: this.sessionId }),
    }).catch(() => {});
  }

  selectSuggestion(text: string): void {
    this.draft = text;
  }

  copyToClipboard(text: string): void {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => {});
    }
  }

  /**
   * Triggered by either license upload button. Stores the picked
   * license_type, then opens the corresponding hidden file input.
   */
  triggerLicenseUpload(license_type: 'state_license' | 'dea_license',
                       input: HTMLInputElement): void {
    if (this.uploadingLicenseType) return;  // already uploading something
    this.uploadError = null;
    input.dataset['licenseType'] = license_type;
    input.value = '';  // allow re-picking the same file
    input.click();
  }

  async onLicenseFilePicked(event: Event,
                            license_type: 'state_license' | 'dea_license'): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    // Lightweight client-side guards — backend revalidates.
    if (file.size > 10 * 1024 * 1024) {
      this.uploadError = `File too large (${(file.size / 1024 / 1024).toFixed(1)} MB > 10 MB cap).`;
      return;
    }
    if (file.type && !['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/heic'].includes(file.type)) {
      this.uploadError = `Unsupported file type: ${file.type}. PDF or image only.`;
      return;
    }

    this.uploadingLicenseType = license_type;
    this.uploadError = null;
    try {
      const attachment = await this.uploadLicenseFile(file, license_type);
      this.pendingAttachments = [...this.pendingAttachments, attachment];
    } catch (e: any) {
      this.uploadError = e?.message || 'Upload failed. Try again.';
    } finally {
      this.uploadingLicenseType = null;
    }
  }

  removeAttachment(index: number): void {
    this.pendingAttachments = this.pendingAttachments.filter((_, i) => i !== index);
  }

  /** Upload a file to the chat-side upload endpoint. Returns the
   *  ChatAttachment to stash in pendingAttachments. */
  private async uploadLicenseFile(file: File,
                                  license_type: 'state_license' | 'dea_license'): Promise<ChatAttachment> {
    const token = localStorage.getItem('auth0Token');
    if (!token || !this.agent?.webhookUrl) {
      throw new Error('Not authenticated.');
    }
    const hostname = window.location.hostname;

    const form = new FormData();
    form.append('doc', file, file.name);
    form.append('license_type', license_type);

    const res = await fetch(this.agent.webhookUrl + '/chat/upload', {
      method: 'POST',
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${token}`,
        SourceDomain: hostname,
        'Auth0-Client-Id': environment.auth0.clientId,
        'Client-Name-Space': environment.supplierConfig.clientNameSpace,
        CatalogType: (environment.catalogType as Record<string, string>)[hostname] || 'medigi',
        // NOTE: do NOT set Content-Type — fetch fills the multipart boundary.
      },
      body: form,
    });
    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}));
      throw new Error(errBody?.error || `Upload failed (HTTP ${res.status}).`);
    }
    const json = await res.json();
    const data = json?.data;
    if (!data?.url) {
      throw new Error('Upload succeeded but returned no URL.');
    }
    return {
      url: data.url,
      filename: data.filename || file.name,
      mime: data.mime || file.type,
      size_bytes: data.size_bytes || file.size,
      license_type,
    };
  }

  /**
   * Trigger a browser download of the full conversation buffer as a
   * .json file. Strips transient UI state (pending, progress,
   * errorStatus) so what you get is what you said + what the agent
   * answered — handy for filing bug reports or sharing a session with
   * engineering.
   */
  downloadConversationAsJson(): void {
    const payload = {
      agent: this.agent?.key,
      session_id: this.sessionId,
      exported_at: new Date().toISOString(),
      messages: (this.messages || [])
        .filter((m) => !m.pending)
        .map((m) => ({
          role: m.role,
          text: m.text,
          personas: m.personas,
          side_effects: m.sideEffects,
          sources: m.sources,
          chart: m.chart,
          error: m.error,
        })),
    };

    const json = JSON.stringify(payload, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const datePart = new Date().toISOString().slice(0, 10);
    const agentKey = this.agent?.key || 'agent';
    const filename = `dr-medas-${agentKey}-${datePart}.json`;

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  getAgentAvatar(m: ChatMessage): string {
    if (m.pending) {
      if (m.progress && m.progress.length > 0) {
        return 'assets/img/ai/medas-analizing.png';
      }
      return 'assets/img/ai/medas-think.png';
    }
    return 'assets/img/ai/medas-found.png';
  }

  copyErrorDetails(failed: ChatMessage): void {
    const idx = this.messages.indexOf(failed);
    const prior = idx > 0 ? this.messages[idx - 1] : null;
    const requestBody: Record<string, any> | null = prior
      ? { chatInput: prior.text, sessionId: this.sessionId }
      : null;
    const body = {
      agent: this.agent.key,
      webhookUrl: this.agent.webhookUrl,
      request: requestBody,
      error: failed.error,
      status: failed.errorStatus,
    };
    const payload = JSON.stringify(body, null, 2);
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(payload).catch(() => {});
    }
  }

  // Cheap markdown-table detector: a row starting with `|` plus at least
  // one separator row (`|---|`). Used to widen the bubble for table-heavy
  // agent responses (mirrors the `.has-chart` pattern).
  hasTable(text: string | undefined): boolean {
    if (!text) return false;
    return /^\s*\|.+\|\s*$/m.test(text) && /^\s*\|?\s*:?-{2,}/m.test(text);
  }

  trackByIndex(_index: number, _item: ChatMessage): number {
    return _index;
  }

  /**
   * SSE-based streaming dispatch for the internal `/api/ai/agent/stream`
   * endpoint. Yields `tool_use` / `tool_done` / `done` / `error` events as the
   * agent progresses. No client-side timeout — the connection stays alive as
   * long as bytes are flowing.
   */
  private dispatchToAgent(chatInput: string, placeholder: ChatMessage,
                          attachments: ChatAttachment[] = []): void {
    this.sending = true;
    placeholder.progress = [];

    const url = this.agent.webhookUrl + '/stream';
    const body = JSON.stringify({
      chatInput,
      sessionId: this.sessionId,
      attachments: attachments.length ? attachments : undefined,
    });

    // Match Auth0HttpInterceptor: read the token straight from localStorage
    // (key `auth0Token`). Calling getTokenSilently() can return a token with
    // a different audience than the one the backend was configured to accept.
    const token = localStorage.getItem('auth0Token');
    if (!token) {
      this.applyError(placeholder, 'Could not obtain auth token. Please log back in.', 401);
      this.sending = false;
      return;
    }

    this.runStream(url, body, token, placeholder);
  }

  private async runStream(
    url: string,
    body: string,
    token: string,
    placeholder: ChatMessage,
  ): Promise<void> {
    let response: Response;
    try {
      // Mirror Auth0HttpInterceptor.cloneRequest — the backend's
      // @auth0_authenticated decorator validates the token against the
      // `Auth0-Client-Id` header, and downstream code expects the catalog
      // headers.
      const hostname = window.location.hostname;
      response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'text/event-stream',
          SourceDomain: hostname,
          'Auth0-Client-Id': environment.auth0.clientId,
          'Client-Name-Space': environment.supplierConfig.clientNameSpace,
          CatalogType: (environment.catalogType as Record<string, string>)[hostname] || 'medigi',
        },
        body,
      });
    } catch {
      this.applyError(placeholder, 'Network error reaching the agent.', 0);
      this.sending = false;
      return;
    }

    if (!response.ok) {
      this.applyError(placeholder, `Agent returned ${response.status}.`, response.status);
      this.sending = false;
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      this.applyError(placeholder, 'Streaming not supported by this browser.', 0);
      this.sending = false;
      return;
    }

    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE frames are separated by blank lines (\n\n).
        const frames = buffer.split('\n\n');
        buffer = frames.pop() ?? '';

        for (const frame of frames) {
          const dataLine = frame.split('\n').find(l => l.startsWith('data:'));
          if (!dataLine) continue;
          const json = dataLine.slice(5).trim();
          if (!json) continue;
          let evt: any;
          try {
            evt = JSON.parse(json);
          } catch {
            continue;
          }
          this.handleStreamEvent(evt, placeholder);
        }
      }
    } catch {
      if (placeholder.pending) {
        this.applyError(placeholder, 'Stream interrupted.', 0);
      }
    } finally {
      this.sending = false;
      if (placeholder.pending) {
        this.applyError(placeholder, 'Stream ended without a final answer.', 0);
      }
    }
  }

  private handleStreamEvent(evt: any, placeholder: ChatMessage): void {
    switch (evt?.type) {
      case 'tool_use': {
        const label = evt.persona || evt.tool || 'specialist';
        placeholder.progress = [...(placeholder.progress || []), `Delegating to ${label}…`];
        this.persist();
        break;
      }
      case 'tool_done': {
        const trail = placeholder.progress || [];
        if (trail.length > 0) {
          const last = trail[trail.length - 1];
          if (last.endsWith('…')) {
            placeholder.progress = [...trail.slice(0, -1), last.replace('…', ' ✓')];
            this.persist();
          }
        }
        break;
      }
      case 'done': {
        placeholder.text = typeof evt.text === 'string' ? evt.text : '';
        placeholder.chart = this.extractChart({ chart: evt.chart });
        placeholder.personas = Array.isArray(evt.personaTrail) ? evt.personaTrail : [];
        placeholder.sources = Array.isArray(evt.sources) && evt.sources.length > 0 ? evt.sources : undefined;
        placeholder.sideEffects = Array.isArray(evt.sideEffects) && evt.sideEffects.length > 0 ? evt.sideEffects : undefined;
        placeholder.progress = undefined;
        placeholder.pending = false;
        if (!placeholder.text) {
          this.applyError(placeholder, 'Agent returned no answer.', 0);
        } else {
          this.persist();
        }
        break;
      }
      case 'error':
        this.applyError(placeholder, evt.message || 'Agent error.', 0);
        break;
    }
  }

  private extractChart(source: { chart?: any } | null | undefined): ChartSpec | undefined {
    const raw = source?.chart;
    if (!raw || typeof raw !== 'object') return undefined;
    const { chartType, argumentField, valueFields, data } = raw;
    if (!['bar', 'line', 'area', 'pie'].includes(chartType)) return undefined;
    if (typeof argumentField !== 'string' || !argumentField) return undefined;
    if (!Array.isArray(valueFields) || valueFields.length === 0) return undefined;
    if (!Array.isArray(data) || data.length === 0) return undefined;
    return { chartType, title: raw.title || '', argumentField, valueFields, data };
  }

  private applyError(placeholder: ChatMessage, msg: string, status: number): void {
    placeholder.pending = false;
    placeholder.error = msg;
    placeholder.errorStatus = status;
    placeholder.text = '';
    placeholder.sources = undefined;
    placeholder.sideEffects = undefined;
    placeholder.chart = undefined;
    placeholder.personas = undefined;
    placeholder.progress = undefined;
    this.persist();
  }

  private pushMessage(msg: ChatMessage): void {
    if (this.messages.length >= BUFFER_CAP) {
      const dropCount = this.messages.length - (BUFFER_CAP - 1);
      this.messages.splice(0, dropCount);
      this.trimmedThisSession = true;
    }
    this.messages.push(msg);
  }

  private get storageKey(): string {
    return `ai-agents:${this.sessionId}:messages`;
  }

  private loadFromStorage(): ChatMessage[] {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as PersistedMessage[];
      if (!Array.isArray(parsed)) return [];
      return parsed
        .filter(m => m && (m.role === 'user' || m.role === 'agent'))
        .map(m => ({ ...m, pending: false }));
    } catch (e) {
      console.warn('ai-agents: failed to load history', e);
      return [];
    }
  }

  private persist(): void {
    try {
      const persistable: PersistedMessage[] = this.messages.map(m => {
        const { pending, ...rest } = m;
        return rest;
      });
      localStorage.setItem(this.storageKey, JSON.stringify(persistable));
    } catch (e) {
      console.warn('ai-agents: persist failed', e);
      if (this.messages.length > 2) {
        this.messages.splice(0, 2);
        try {
          const persistable: PersistedMessage[] = this.messages.map(({ pending, ...rest }) => rest);
          localStorage.setItem(this.storageKey, JSON.stringify(persistable));
        } catch {
          // give up silently
        }
      }
    }
  }

  private clearStorage(): void {
    try {
      localStorage.removeItem(this.storageKey);
    } catch {
      // no-op
    }
  }
}
